import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Calendar, 
    List, 
    Typography, 
    Button, 
    Modal, 
    Form, 
    Input, 
    Select, 
    DatePicker, 
    TimePicker,
    Space,
    Tag,
    Badge,
    Tooltip,
    Popover,
    Divider,
    Row,
    Col
} from 'antd';
import { 
    CalendarOutlined, 
    PlusOutlined, 
    EditOutlined, 
    DeleteOutlined,
    ClockCircleOutlined,
    EnvironmentOutlined,
    UserOutlined,
    BellOutlined,
    SyncOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CalendarIntegration = ({ 
    showAddEvent = true,
    showSyncOptions = true,
    compact = false
}) => {
    const { user } = useAuth();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [eventModalOpen, setEventModalOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchEvents();
    }, [selectedDate]);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            const response = await api.getCalendarEvents({
                month: selectedDate.month() + 1,
                year: selectedDate.year()
            });
            
            if (response.success) {
                setEvents(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch events:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDateSelect = (date) => {
        setSelectedDate(date);
    };

    const handleEventClick = (event) => {
        setEditingEvent(event);
        form.setFieldsValue({
            title: event.title,
            description: event.description,
            start_date: dayjs(event.start_date),
            end_date: dayjs(event.end_date),
            start_time: dayjs(event.start_time, 'HH:mm'),
            end_time: dayjs(event.end_time, 'HH:mm'),
            location: event.location,
            type: event.type,
            reminder: event.reminder
        });
        setEventModalOpen(true);
    };

    const handleAddEvent = () => {
        setEditingEvent(null);
        form.resetFields();
        setEventModalOpen(true);
    };

    const handleSubmitEvent = async (values) => {
        try {
            const eventData = {
                title: values.title,
                description: values.description,
                start_date: values.start_date.format('YYYY-MM-DD'),
                end_date: values.end_date.format('YYYY-MM-DD'),
                start_time: values.start_time.format('HH:mm'),
                end_time: values.end_time.format('HH:mm'),
                location: values.location,
                type: values.type,
                reminder: values.reminder
            };

            let response;
            if (editingEvent) {
                response = await api.updateCalendarEvent(editingEvent.id, eventData);
            } else {
                response = await api.createCalendarEvent(eventData);
            }

            if (response.success) {
                setEventModalOpen(false);
                form.resetFields();
                fetchEvents();
            }
        } catch (error) {
            console.error('Failed to save event:', error);
        }
    };

    const handleDeleteEvent = async (eventId) => {
        try {
            const response = await api.deleteCalendarEvent(eventId);
            if (response.success) {
                fetchEvents();
            }
        } catch (error) {
            console.error('Failed to delete event:', error);
        }
    };

    const getEventTypeColor = (type) => {
        const colors = {
            'booking': 'blue',
            'quote': 'green',
            'personal': 'purple',
            'reminder': 'orange',
            'meeting': 'cyan'
        };
        return colors[type] || 'default';
    };

    const getEventTypeIcon = (type) => {
        const icons = {
            'booking': <CalendarOutlined />,
            'quote': <ClockCircleOutlined />,
            'personal': <UserOutlined />,
            'reminder': <BellOutlined />,
            'meeting': <EnvironmentOutlined />
        };
        return icons[type] || <CalendarOutlined />;
    };

    const getListData = (value) => {
        const dateStr = value.format('YYYY-MM-DD');
        return events.filter(event => 
            event.start_date === dateStr || 
            (event.end_date && event.end_date >= dateStr && event.start_date <= dateStr)
        );
    };

    const dateCellRender = (value) => {
        const listData = getListData(value);
        return (
            <div className="calendar-events">
                {listData.map((event, index) => (
                    <div
                        key={index}
                        className="calendar-event-item"
                        onClick={() => handleEventClick(event)}
                    >
                        <Badge
                            color={getEventTypeColor(event.type)}
                            text={
                                <div className="flex items-center gap-1">
                                    {getEventTypeIcon(event.type)}
                                    <span className="text-xs truncate">{event.title}</span>
                                </div>
                            }
                        />
                    </div>
                ))}
            </div>
        );
    };

    const renderEventDetails = (event) => (
        <div className="space-y-2">
            <div className="flex items-center gap-2">
                {getEventTypeIcon(event.type)}
                <Text strong>{event.title}</Text>
                <Tag color={getEventTypeColor(event.type)} size="small">
                    {event.type}
                </Tag>
            </div>
            
            {event.description && (
                <Text type="secondary" className="text-sm">
                    {event.description}
                </Text>
            )}
            
            <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm">
                    <ClockCircleOutlined />
                    <Text>
                        {dayjs(event.start_date).format('MMM DD, YYYY')} at {event.start_time}
                    </Text>
                </div>
                
                {event.location && (
                    <div className="flex items-center gap-2 text-sm">
                        <EnvironmentOutlined />
                        <Text>{event.location}</Text>
                    </div>
                )}
            </div>
            
            <div className="flex gap-2">
                <Button 
                    size="small" 
                    icon={<EditOutlined />}
                    onClick={() => handleEventClick(event)}
                >
                    Edit
                </Button>
                <Button 
                    size="small" 
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteEvent(event.id)}
                >
                    Delete
                </Button>
            </div>
        </div>
    );

    const renderCompactView = () => (
        <Card 
            title="Upcoming Events" 
            size="small"
            extra={
                <Button 
                    type="primary" 
                    size="small" 
                    icon={<PlusOutlined />}
                    onClick={handleAddEvent}
                >
                    Add
                </Button>
            }
        >
            <List
                dataSource={events.slice(0, 5)}
                renderItem={(event) => (
                    <List.Item>
                        <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-2">
                                <Badge 
                                    color={getEventTypeColor(event.type)} 
                                    text={event.title}
                                />
                                <Text type="secondary" className="text-xs">
                                    {dayjs(event.start_date).format('MMM DD')}
                                </Text>
                            </div>
                            <Button 
                                type="text" 
                                size="small"
                                onClick={() => handleEventClick(event)}
                            >
                                View
                            </Button>
                        </div>
                    </List.Item>
                )}
            />
        </Card>
    );

    if (compact) {
        return renderCompactView();
    }

    return (
        <div className="calendar-integration">
            <Card
                title={
                    <div className="flex justify-between items-center">
                        <span>Calendar</span>
                        <Space>
                            {showSyncOptions && (
                                <Button icon={<SyncOutlined />}>
                                    Sync
                                </Button>
                            )}
                            {showAddEvent && (
                                <Button 
                                    type="primary" 
                                    icon={<PlusOutlined />}
                                    onClick={handleAddEvent}
                                >
                                    Add Event
                                </Button>
                            )}
                        </Space>
                    </div>
                }
            >
                <Calendar
                    dateCellRender={dateCellRender}
                    onSelect={handleDateSelect}
                    value={selectedDate}
                />
            </Card>

            <Modal
                title={editingEvent ? 'Edit Event' : 'Add Event'}
                open={eventModalOpen}
                onCancel={() => setEventModalOpen(false)}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmitEvent}
                >
                    <Form.Item
                        name="title"
                        label="Event Title"
                        rules={[{ required: true, message: 'Please enter event title' }]}
                    >
                        <Input placeholder="Enter event title" />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <TextArea rows={3} placeholder="Enter event description" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="start_date"
                                label="Start Date"
                                rules={[{ required: true, message: 'Please select start date' }]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="end_date"
                                label="End Date"
                                rules={[{ required: true, message: 'Please select end date' }]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="start_time"
                                label="Start Time"
                                rules={[{ required: true, message: 'Please select start time' }]}
                            >
                                <TimePicker style={{ width: '100%' }} format="HH:mm" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="end_time"
                                label="End Time"
                                rules={[{ required: true, message: 'Please select end time' }]}
                            >
                                <TimePicker style={{ width: '100%' }} format="HH:mm" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="location"
                        label="Location"
                    >
                        <Input placeholder="Enter event location" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="type"
                                label="Event Type"
                                rules={[{ required: true, message: 'Please select event type' }]}
                            >
                                <Select placeholder="Select event type">
                                    <Option value="booking">Booking</Option>
                                    <Option value="quote">Quote</Option>
                                    <Option value="personal">Personal</Option>
                                    <Option value="reminder">Reminder</Option>
                                    <Option value="meeting">Meeting</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="reminder"
                                label="Reminder"
                            >
                                <Select placeholder="Select reminder">
                                    <Option value="none">No Reminder</Option>
                                    <Option value="15m">15 minutes before</Option>
                                    <Option value="1h">1 hour before</Option>
                                    <Option value="1d">1 day before</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <div className="flex justify-end gap-2">
                        <Button onClick={() => setEventModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button type="primary" htmlType="submit">
                            {editingEvent ? 'Update' : 'Create'} Event
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default CalendarIntegration;
